/**
 * Passkey / WebAuthn Service for Red Bridge Farm Feed Co.
 *
 * Provides biometric login (Face ID, Touch ID, Windows Hello, Fingerprint)
 * using the WebAuthn standard.
 */

import { setTokens, setUserData, getAccessToken, UserData } from './authService';

// Normalize API_BASE - remove trailing /api if present to avoid double /api/api
// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_RAW = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\.+\/*$/, '').replace(/\/+$/, '');
const API_BASE = API_BASE_RAW.replace(/\/api\/?$/, '');

// ===== WEBAUTHN AVAILABILITY =====

/**
 * Check if WebAuthn is supported by the browser
 */
export const isWebAuthnSupported = (): boolean => {
  return !!(window.PublicKeyCredential);
};

/**
 * Check if platform authenticator is available (Face ID, Touch ID, Windows Hello)
 */
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

/**
 * Check if server has WebAuthn configured
 */
export const isServerWebAuthnAvailable = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${API_BASE}/api/passkey/status/`);
    if (res.ok) {
      const data = await res.json();
      return data.available;
    }
  } catch {
    // Ignore
  }
  return false;
};

// ===== REGISTER NEW PASSKEY =====

export interface PasskeyResult {
  success: boolean;
  error?: string;
  device_name?: string;
}

export const registerPasskey = async (deviceName: string): Promise<PasskeyResult> => {
  const accessToken = getAccessToken();
  if (!accessToken) return { success: false, error: 'Not logged in' };

  try {
    // 1. Get registration options from server
    const optionsRes = await fetch(`${API_BASE}/api/passkey/register/options/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify({ device_name: deviceName }),
    });

    if (!optionsRes.ok) {
      const err = await optionsRes.json();
      throw new Error(err.error || 'Failed to get registration options');
    }
    const options = await optionsRes.json();

    // 2. Create credential (triggers biometric prompt)
    const publicKeyOptions: PublicKeyCredentialCreationOptions = {
      challenge: base64URLToBuffer(options.challenge),
      rp: options.rp,
      user: {
        id: base64URLToBuffer(options.user.id),
        name: options.user.name,
        displayName: options.user.displayName,
      },
      pubKeyCredParams: options.pubKeyCredParams,
      timeout: options.timeout,
      authenticatorSelection: options.authenticatorSelection,
      attestation: options.attestation,
      excludeCredentials: options.excludeCredentials?.map((c: { id: string; type: string }) => ({
        id: base64URLToBuffer(c.id),
        type: c.type,
      })),
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential;

    if (!credential) throw new Error('Failed to create credential');

    const response = credential.response as AuthenticatorAttestationResponse;

    // 3. Send to server for verification
    const verifyRes = await fetch(`${API_BASE}/api/passkey/register/verify/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify({
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64URL(response.clientDataJSON),
          attestationObject: bufferToBase64URL(response.attestationObject),
        },
      }),
    });

    return await verifyRes.json();

  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Biometric setup was cancelled' };
      }
      return { success: false, error: error.message || 'Failed to register passkey' };
    }
    return { success: false, error: 'Failed to register passkey' };
  }
};

// ===== AUTHENTICATE WITH PASSKEY =====

export interface PasskeyAuthResult {
  success: boolean;
  error?: string;
  user?: UserData;
}

export const authenticateWithPasskey = async (email?: string): Promise<PasskeyAuthResult> => {
  try {
    // 1. Get auth options from server
    const optionsRes = await fetch(`${API_BASE}/api/passkey/auth/options/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (optionsRes.status === 423) {
      const data = await optionsRes.json();
      return { success: false, error: data.detail };
    }

    if (!optionsRes.ok) {
      const err = await optionsRes.json();
      throw new Error(err.error || 'Failed to get auth options');
    }
    const options = await optionsRes.json();

    // 2. Get credential (triggers biometric prompt)
    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64URLToBuffer(options.challenge),
      timeout: options.timeout,
      rpId: options.rpId,
      allowCredentials: options.allowCredentials?.map((c: { id: string; type: string }) => ({
        id: base64URLToBuffer(c.id),
        type: c.type,
      })),
      userVerification: options.userVerification,
    };

    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential;

    if (!credential) throw new Error('Authentication failed');

    const response = credential.response as AuthenticatorAssertionResponse;

    // 3. Verify with server
    const verifyRes = await fetch(`${API_BASE}/api/passkey/auth/verify/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64URL(response.clientDataJSON),
          authenticatorData: bufferToBase64URL(response.authenticatorData),
          signature: bufferToBase64URL(response.signature),
          userHandle: response.userHandle ? bufferToBase64URL(response.userHandle) : null,
        },
      }),
    });

    const result = await verifyRes.json();

    if (result.success) {
      setTokens(result.access, result.refresh);
      setUserData(result.user);
    }

    return result;

  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Authentication cancelled' };
      }
      return { success: false, error: error.message || 'Biometric login failed' };
    }
    return { success: false, error: 'Biometric login failed' };
  }
};

// ===== LIST PASSKEYS =====

export interface Passkey {
  id: number;
  device_name: string;
  created_at: string;
  last_used_at: string | null;
}

export const listPasskeys = async (): Promise<Passkey[]> => {
  const token = getAccessToken();
  if (!token) return [];

  try {
    const res = await fetch(`${API_BASE}/api/passkey/list/`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) return await res.json();
  } catch {
    // Ignore
  }
  return [];
};

// ===== DELETE PASSKEY =====

export const deletePasskey = async (id: number): Promise<boolean> => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const res = await fetch(`${API_BASE}/api/passkey/${id}/delete/`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
};

// ===== HELPER FUNCTIONS =====

function base64URLToBuffer(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

function bufferToBase64URL(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
